<template>
  <form
    class="multi-store-modal"
    :class="`multi-store-modal--${template}`"
    :action="action"
    method="post"
    @submit.prevent="handleLocalizationFormSubmit"
  >
    <!-- Custom close button to bind click event -->
    <button
      class="
        overlay__close
        modal__close
        multi-store-modal-overlay__close
        icon-button
      "
      type="button"
      @click.prevent="handleCloseOverlay"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.close')"
      />

      <icon-close />
    </button>

    <input
      name="_method"
      type="hidden"
      value="put"
    >

    <input
      name="return_to"
      type="hidden"
      :value="returnTo"
    >

    <!-- Options -->
    <template v-if="template === 'options'">
      <p
        class="multi-store-modal__options-title text-heading-m-bold"
        v-text="$string('general.multi_store.options.title')"
      />

      <p
        class="multi-store-modal__options-text text-body-m"
        v-html="$string('general.multi_store.options.choose_your_preferences')"
      />

      <multi-store-options
        class="multi-store-modal__options-selects col xs6 l3-8 xl5-9"
        namespace="modal"
      />
    </template>

    <!-- Suggestions -->
    <div
      v-else
      class="multi-store-modal__suggestions"
    >
      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        class="multi-store-modal__flag"
        alt=""
        height="40"
        role="presentation"
        :src="flag.src"
        :srcset="flag.srcset"
      >

      <p
        class="multi-store-modal__suggestions-title text-heading-m-bold"
        v-html="suggestionStrings.title"
      />

      <p
        class="multi-store-modal__suggestions-text text-body-m"
        v-html="suggestionStrings.text"
      />

      <ul class="multi-store-modal__suggestions-items text-body-m">
        <li
          class="multi-store-modal__suggestions-item"
          v-text="suggestionStrings.currency"
        />

        <li
          class="multi-store-modal__suggestions-item"
          v-text="suggestionStrings.shipping"
        />

        <li
          class="multi-store-modal__suggestions-item"
          v-text="suggestionStrings.language"
        />
      </ul>

      <!-- Hidden inputs to set continue values -->
      <input
        name="country_code"
        type="hidden"
        :value="suggestedStore.country.code"
      >

      <input
        name="language_code"
        type="hidden"
        :value="suggestedStore.language.code"
      >
    </div>

    <!-- Footer -->
    <div class="multi-store-modal__footer">
      <btn
        class="multi-store-modal__continue"
        :label="submitButtonString"
        modifiers="block"
        type="submit"
      />

      <button
        class="multi-store-modal__alt-button text-body-m-link"
        type="button"
        @click.prevent="handleAltButtonClick"
        v-text="altButtonString"
      />
    </div>
  </form>
</template>

<script>

/**
 * Vue: Multi-store modal (multi-store-modal)
 * -----------------------------------------------------------------------------
 * Modal to control multi-store settings.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import MultiStoreOptions from '~global/multi-store-options/multi-store-options'

import IconClose from '~icons/misc/close.svg'

import { multiStoreAction, multiStoreReturnTo } from '~/helpers/general'

export default {
  name: 'MultiStoreModal',

  components: {
    Btn,
    IconClose,
    MultiStoreOptions,
  },

  data() {
    return {
      namespace: 'multiStoreModal',
      template: 'suggestions',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
    }),

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore || this.suggestedStore
    },

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute alt button string.
     * @returns {String}
     */
    altButtonString() {
      return this.template === 'suggestions'
        ? this.$string('general.multi_store.suggestions.change')
        : this.$string('general.multi_store.options.cancel')
    },

    /**
     * Compute flag URL.
     * @returns {String}
     */
    flag() {
      const code = this.suggestedStore.country.code?.toLowerCase()

      return {
        src: `https://flagcdn.com/h40/${code}.png`,
        srcset: `https://flagcdn.com/h80/${code}.png 2x, https://flagcdn.com/h120/${code}.png 3x`,
      }
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      return multiStoreReturnTo(this.active)
    },

    /**
     * Compute submit button string.
     * @returns {String}
     */
    submitButtonString() {
      return this.template === 'suggestions'
        ? this.$string(
          'general.multi_store.suggestions.continue_html',
          { replace: this.replace() },
        )
        : this.$string('general.multi_store.options.continue')
    },

    /**
     * Compute suggestion strings.
     * @returns {Object}
     */
    suggestionStrings() {
      return {
        currency: this.$string(
          'general.multi_store.suggestions.currency_html',
          { replace: this.replace() },
        ),
        language: this.$string(
          'general.multi_store.suggestions.language_html',
          { replace: this.replace() },
        ),
        shipping: this.$string(
          'general.multi_store.suggestions.shipping_html',
          { replace: this.replace() },
        ),
        text: this.$string(
          'general.multi_store.suggestions.text_html',
          { replace: this.replace() },
        ),
        title: this.$string(
          'general.multi_store.suggestions.title_html',
          { replace: this.replace() },
        ),
      }
    },
  },

  watch: {

    /**
     * Watch template changing to set selected store back to suggested store.
     */
    template() {
      if (this.template !== 'suggestions') {
        return
      }

      this.setSelectedStore(this.suggestedStore)
    },
  },

  mounted() {
    cnvs.EventBus.emit('overlay:update', 'multiStoreModal')
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Handle localization form submit.
     * @param {Object} event - Submit event.
     */
    async handleLocalizationFormSubmit(event) {
      const form = event.target
      const formData = new FormData(form)

      this.setPreference({
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      })

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },

    /**
     * Handle alt button click.
     */
    handleAltButtonClick() {
      this.template = this.template === 'suggestions'
        ? 'options'
        : 'suggestions'

      nextTick().then(() => {
        cnvs.EventBus.emit('overlay:update', this.namespace)
      })
    },

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replace() {
      const currency = `${this.suggestedStore.country.currency} ${this.suggestedStore.country.currencySymbol}`

      return {
        country_name: this.suggestedStore.country.name,
        currency_name: currency,
        language_name: this.suggestedStore.language.name,
        the_country_name: this.suggestedStore.country.the
          ? `${this.$string('general.multi_store.the')} ${this.suggestedStore.country.name}`
          : this.suggestedStore.country.name,
      }
    },

    /**
     * Handle close overlay click.
     */
    handleCloseOverlay() {
      const data = {
        country: this.current.country.code,
        language: this.current.language.code,
      }

      sessionStorage.setItem('multi-store-dismissed', JSON.stringify(data))
      this.closeOverlay(this.namespace)
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-modal';
</style>
